// src/app/features/dashboard/widgets/ScarletWell/ScarletWell.js
import * as React from 'react'
import { Box, Button, Card } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Widget from '../Widget'
import {
  fetchScarletWellData as fetchScarletWell,
  fetchScarletWellFirebaseData as fetchScarletWellFirebase,
  selectScarletWellData,
  selectScarletWellStatus,
  fetchScarletWellCheckInData,
} from './scarletWellSlice'
import scarletWellWidgetStyles from './scarletWellWidgetStyles'
import Loading from '../helpers/Loading'
import { ErrorMessage } from '../../DashboardComponents/ErrorMessages/ErrorMsg'
import SearchBar from '../../DashboardComponents/SearchBar/SearchBar'
import {
  RutgersTab,
  RutgersTabs,
} from '../../DashboardComponents/Tabs/RutgersTabs'
import {
  TabPanel,
  a11yProps,
} from '../../DashboardComponents/TabPanel/TabPanel'
import SWeventList from './ScarletWellEventList'
import IconPicker from '../../../apps/AppComponents/IconPicker/IconPicker'
import { logDashboardEvent } from '../../events'
import WidgetFooterButton from '../CommuterResources/WidgetFooterButton'
import { selectMyProfileData } from '../MyProfile/myProfileSlice'
import { selectDarkMode } from '../../../settings/settingsSlice'
import { useTabTracking } from '../../../../helpers/useTabTracking'
import { trackWidgetClick } from '../../../../helpers/trackWidgetClick'

function ScarletWell({
  className,
  title,
  showNote,
  noteMessage,
  notePosition,
  noteBackground,
  noteIcon,
  noteUrl,
  noteFontColor,
  widgetId,
}) {
  const dispatch = useDispatch()
  const widgetData = useSelector(selectScarletWellData)
  const widgetCallState = useSelector(selectScarletWellStatus)
  const isDark = useSelector(selectDarkMode)

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchScarletWellFirebase()),
          dispatch(fetchScarletWell()),
          dispatch(fetchScarletWellCheckInData()),
        ])
      } catch (err) {
        console.log('Error fetching Scarlet Well Data', err)
      }
    }
    fetchData()
  }, [])

  return (
    <Widget className={className}>
      <Widget.Header title={title} category="default" />
      <Widget.Content
        showNote={showNote}
        noteMessage={noteMessage}
        notePosition={notePosition}
        noteBackground={noteBackground}
        noteIcon={noteIcon}
        noteUrl={noteUrl}
        noteFontColor={noteFontColor}
      >
        {renderScarletWellState(
          widgetCallState,
          widgetData,
          isDark,
          title,
          widgetId,
        )}
      </Widget.Content>
    </Widget>
  )
}

function renderScarletWellState(
  widgetCallState,
  widgetData,
  isDark,
  title,
  widgetId,
) {
  switch (widgetCallState) {
    case 'fulfilled':
      return (
        <Loaded
          widgetData={widgetData}
          isDark={isDark}
          title={title}
          widgetId={widgetId}
        />
      )
    case 'pending':
      return <Loading />
    case 'error':
      return (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <ErrorMessage />
        </Grid>
      )
    default:
      return (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item></Grid>
        </Grid>
      )
  }
}

function Loaded({ widgetData, isDark, title, widgetId }) {
  const profileData = useSelector(selectMyProfileData)
  const name = profileData.firstName
  const classes = scarletWellWidgetStyles()
  const events = widgetData && widgetData.events ? widgetData.events : []
  const [searchQuery, setSearchQuery] = React.useState('')
  const dispatch = useDispatch()
  const history = useHistory()
  const [activeWellnessIndex, setActiveWellnessIndex] = React.useState(0)

  const categories = widgetData?.checkInPage?.categories || []

  const [learnMoreUrl, setLearnMoreUrl] = React.useState(
    categories[0]?.categoryLink || '',
  )

  // call search url with query string in query link
  const handleSearchQuery = (searchTerm) => {
    trackWidgetClick(dispatch, 'ScarletWell', 'Search', { widgetId })
    const redirectString = searchTerm.toString().split(' ').join('%20')
    window.open(
      `${widgetData.eventsTab?.searchBaseURL}${redirectString}`,
      '_blank',
    )
  }
  const [currentTable, setCurrentTable] = React.useState(0)
  const trackTabChange = useTabTracking(
    dispatch,
    'ScarletWell',
    ['Events', 'Check-in'],
    { widgetId },
  )
  const handleChange = (event, newValue) => {
    trackTabChange(event, newValue, () => setCurrentTable(newValue))
  }

  const handleClick = (url, objectId) => {
    const objectID = objectId ?? title
    trackWidgetClick(dispatch, 'ScarletWell', objectID, { widgetId })
    if (widgetId && dispatch) {
      logDashboardEvent(dispatch, 'dashboardLinkEvent', widgetId, objectID, url)
      window.open(url)
    }
  }

  const handleCheckInClick = () => {
    const isLocalhost = window.location.hostname === 'localhost'
    const path = isLocalhost
      ? widgetData.checkInTab?.mainButton
      : `${process.env.REACT_APP_BASENAME}${widgetData.checkInTab?.mainButton}`
    if (widgetId && dispatch) {
      logDashboardEvent(
        dispatch,
        'dashboardLinkEvent',
        widgetId,
        'Check in Button',
        widgetData.checkInTab?.mainButton,
      )
    }
    window.open(path, '_self')
  }

  const handleTotalScoreClick = () => {
    history.push('/ScarletWellTotalScore')
  }

  const handleLearnMoreClick = () => {
    window.open(learnMoreUrl, '_blank')
  }

  const mappedWellnessCategories = categories.map((categoryData) => ({
    categoryId: categoryData.categoryId,
    categoryTitle: categoryData.categoryTitle,
    categoryIcon: categoryData.categoryIcon,
    categoryLink: categoryData.categoryLink,
    categoryScore: categoryData.scoreAsDecimal,
  }))

  const activeCategory =
    mappedWellnessCategories[activeWellnessIndex] || mappedWellnessCategories[0]

  return (
    <Box
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      className={classes.gridContainer}
    >
      <Box item className={classes.modalTabs}>
        <RutgersTabs
          value={currentTable}
          onChange={handleChange}
          aria-label="Top Tab"
        >
          <RutgersTab label="Events" {...a11yProps(0)} />
          <RutgersTab label="Check-in" {...a11yProps(1)} />
        </RutgersTabs>
        <TabPanel value={currentTable} index={0}>
          <Box
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Box item className={classes.searchBarContainer}>
              <Button
                aria-label="Calendar"
                onClick={() =>
                  handleClick(
                    widgetData.eventsTab?.calendarLink,
                    'Calendar Button',
                  )
                }
                className={classes.searchBarButton}
              >
                <IconPicker
                  iconName="far fa-calendar"
                  color={isDark ? '#eeeeee' : '#cc0033'}
                  size="2x"
                />
              </Button>

              <Box className={classes.searchBar}>
                <SearchBar
                  placeholder="Search Events"
                  value={searchQuery}
                  webViewMode
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onRequestSearch={() => handleSearchQuery(searchQuery)}
                  onCancelSearch={() => setSearchQuery('')}
                />
              </Box>
              <Button
                aria-label="Filter"
                onClick={() =>
                  handleClick(widgetData.eventsTab?.filterLink, 'Filter Button')
                }
                className={classes.searchBarButton}
              >
                <IconPicker
                  iconName="far fa-filter"
                  color={isDark ? '#eeeeee' : '#cc0033'}
                  size="2x"
                />
              </Button>
            </Box>
            <SWeventList
              events={events}
              handleClick={handleClick}
              backupData={null}
            />
          </Box>
        </TabPanel>
        <TabPanel value={currentTable} index={1} className={classes.tabPanel1}>
          <Box className={classes.checkinRoot}>
            <Box>
              <img
                className={classes.mainImage}
                src={widgetData.checkInTab?.checkInImage}
                alt="check in"
              />
              <Card variant="outlined" className={classes.checkInCard}>
                <Typography
                  variant="h4"
                  component="div"
                  className={classes.checkinTextGreeting}
                >
                  Hello {name}!
                </Typography>
                <Typography variant="body1" className={classes.checkinText}>
                  {widgetData.checkInTab?.checkInText}
                </Typography>
                <WidgetFooterButton
                  title={widgetData.checkInTab?.checkInButtonText}
                  handleClick={handleCheckInClick}
                />
              </Card>
              {/* Wellness Engagement Score Card */}
              <Card variant="outlined" className={classes.engagementCard}>
                <Typography variant="h6" className={classes.engagementHeader}>
                  Wellness Engagement Score
                </Typography>

                {/* 8 Clickable Icons */}
                <Box className={classes.iconRow}>
                  {mappedWellnessCategories.map((cat, index) => {
                    const isActive = index === activeWellnessIndex
                    return (
                      <Box
                        key={cat.categoryId}
                        onClick={() => {
                          setActiveWellnessIndex(index)
                          setLearnMoreUrl(cat.categoryLink)
                        }}
                        className={`${classes.iconWrapperBase} ${
                          isActive
                            ? classes.activeIconWrapper
                            : classes.inactiveIconWrapper
                        }`}
                        role="button"
                        aria-pressed={isActive}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setActiveWellnessIndex(index)
                            setLearnMoreUrl(cat.categoryLink)
                          }
                        }}
                      >
                        <IconPicker
                          iconName={cat.categoryIcon}
                          color={isActive ? '#ffffff' : '#000000'}
                          size="1x"
                        />
                      </Box>
                    )
                  })}
                </Box>

                {/* Score and Title Card */}
                <Box className={classes.scoreCardInnerRow}>
                  <Typography variant="h4" className={classes.scoreText}>
                    {activeCategory?.categoryTitle
                      ? `${activeCategory.categoryTitle}:`
                      : 'Loading:'}
                  </Typography>
                  <Card variant="outlined" className={classes.scoreCardWrapper}>
                    <Typography variant="h4" className={classes.scoreText}>
                      {activeCategory?.categoryScore != null
                        ? activeCategory.categoryScore.toFixed(2)
                        : '0.00'}
                    </Typography>
                  </Card>
                </Box>

                {/* Info & Button Row */}
                <Box className={classes.infoRow}>
                  <Typography variant="body1" className={classes.infoText}>
                    More information about{' '}
                    <b>
                      {activeCategory?.categoryTitle
                        ? activeCategory.categoryTitle.toLowerCase()
                        : 'loading'}
                    </b>{' '}
                    wellness
                  </Typography>
                  <Button
                    variant="contained"
                    className={classes.learnMoreBtn}
                    onClick={handleLearnMoreClick}
                  >
                    Learn more
                  </Button>
                </Box>
              </Card>
              <Button
                variant="contained"
                className={classes.totalScoreBtn}
                onClick={handleTotalScoreClick}
              >
                <Typography variant="h6">View Total Score</Typography>
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Box>
    </Box>
  )
}

export default ScarletWell
